import {
  Box,
  Flex,
  Image,
  JaneGoldLogo,
  Link,
  PartnerLogo,
  Typography,
} from '@iheartjane/reefer';
import { useState } from 'react';

import styles from '../specialDetailStatus.module.css';

/** Props for the special detail header displaying title, description, brand info, and terms */
export interface SpecialDetailStatusHeaderProps {
  /** URL of the brand logo image */
  brandImage?: string;
  /** Brand name — when provided, renders Jane Gold branding */
  brandName?: string;
  /** URL of the promotional header image */
  headerImageSrc?: string;
  /** Description text displayed below the title */
  specialDescription: string;
  /** Terms and conditions text, shown in a collapsible section */
  specialTerms?: string;
  /** Title of the special */
  specialTitle: string;
  /** Type label displayed above the title (e.g., "Bundle", "Product") */
  specialType: string;
}

export const SpecialDetailStatusHeader = ({
  brandImage,
  brandName,
  headerImageSrc,
  specialDescription,
  specialTitle,
  specialTerms,
  specialType,
}: SpecialDetailStatusHeaderProps) => {
  const [showTerms, setShowTerms] = useState(false);

  const isGold = !!brandName;
  const isGoldBundle = isGold && specialType !== 'product';

  return (
    <Box className={styles['header__border']} border="transparent-black-ten">
      {!!headerImageSrc && (
        <Image
          width="100%"
          altText={`Promotional image for ${specialTitle}`}
          src={headerImageSrc}
          sizes="card-lg"
          responsive
          aspectRatio={2 / 1}
          borderRadius="24px 24px 0 0"
          className={styles['header__image']}
        />
      )}
      <Box p={24}>
        {isGold && (
          <Flex justifyContent="space-between" pb={16}>
            {brandName && (
              <PartnerLogo
                name={brandName}
                image={brandImage}
                variant="brand"
                size="xs"
                data-testid="partner-logo"
              />
            )}

            <JaneGoldLogo data-testid="jane-gold-logo" height={26} />
          </Flex>
        )}

        <Typography as="span" color={isGold ? 'grays-mid' : 'info'}>
          {brandName && brandName}
          {brandName && isGoldBundle && (
            <Typography mx={4} as="span" color="grays-mid">
              &#8226;
            </Typography>
          )}
          {isGold && isGoldBundle && specialType}
          {!isGold && specialType}
        </Typography>

        <Typography branded variant="header-bold">
          {specialTitle}
        </Typography>

        <Typography>{specialDescription}</Typography>

        {specialTerms && (
          <Link color="grays-mid" onClick={() => setShowTerms((prev) => !prev)}>
            Terms and conditions
          </Link>
        )}

        {specialTerms && showTerms && (
          <Typography pt={16} color="grays-mid">
            {specialTerms}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
